import React, { useEffect, useState } from "react";
import {
  ArrowUpDown,
  Filter,
  ChevronRight,
  Plane,
  Award,
  DollarSign,
  Clock,
  Sparkles,
  Milestone,
  CheckCircle2,
  TrendingUp,
  Flame,
  ChevronLeft,
  Armchair,
  UtensilsCrossed,
  Heart,
  Share2,
  Info,
  Edit2,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { decryptData } from "../../../utils/crypto";
import { getFlightResultList } from "../../../services/Front/flightService";
import { notify } from "../../../components/Common/Alerts";
import Utils from "../../../utils";
import { useSearch } from "../../../context/SearchContext";
import moment from "moment";

const RedemptionResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state;
  const { formData } = useSearch();

  const [summary, setSummary] = useState(null);
  const [bestDeal, setBestDeal] = useState(null);
  const [flights, setFlights] = useState({
    outbound: [],
    return: [],
    isRoundTrip: false,
  });
  const [sortBy, setSortBy] = useState("cpp");
  const [loading, setLoading] = useState(true);

  const getBadgeText = (score) => {
    if (score >= 80) return "🔥 Best Value";
    if (score >= 65) return "⭐ Good Deal";
    return "⚠ Standard";
  };

  useEffect(() => {
    if (!data) return;

    const obj = {
      search_id: data.search?.search_id,
      card_id: data.search?.card_id,
    };
    setLoading(true);

    getFlightResultList(obj)
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        const apiData = decrypted?.data || {};

        setSummary(apiData?.redeem_summary || null);
        setBestDeal(apiData?.best_deal || null);

        const bestDealId = apiData?.best_deal?.flight_id;

        const normalize = (list, type) =>
          (list || []).map((f, i) => ({
            ...f,
            rank: i + 1,
            route: `${f.departure_airport} → ${f.arrival_airport}`,
            time: `${formatTime(f.departure_time)} - ${formatTime(f.arrival_time)}`,
            durationText: formatDuration(f.total_duration),
            stopsText: f.stops === 0 ? "Nonstop" : `${f.stops} Stop`,
            priceText: money(f.price),
            pointsText: `${Number(f.required_points).toLocaleString()} pts`,
            badgeText: getBadgeText(f.value_score),
            isBest: f.flight_id === bestDealId,
            segment_type: type, // IMPORTANT: OUTBOUND / RETURN
          }));

        setFlights({
          outbound: normalize(apiData?.outbound, "OUTBOUND"),
          return: normalize(apiData?.return, "RETURN"),
          isRoundTrip: apiData?.is_round_trip,
        });
        setLoading(false);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
        setLoading(false);
      });
  }, [data]);

  const handleSort = () => {
    const nextSort =
      sortBy === "cpp" ? "price" : sortBy === "price" ? "duration" : "cpp";

    setSortBy(nextSort);

    const sortFlights = (arr) => {
      const sorted = [...arr];

      if (nextSort === "cpp") {
        sorted.sort((a, b) => b.cpp_value - a.cpp_value);
      }

      if (nextSort === "price") {
        sorted.sort((a, b) => a.price - b.price);
      }

      if (nextSort === "duration") {
        sorted.sort((a, b) => a.total_duration - b.total_duration);
      }

      return sorted;
    };

    setFlights((prev) => ({
      ...prev,
      outbound: sortFlights(prev.outbound),
      return: sortFlights(prev.return),
    }));

    notify.success(`Sorted by ${nextSort.toUpperCase()}`);
  };

  const formatDuration = (min) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h}h ${m}m`;
  };

  const formatTime = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    try {
      const timePart = dateTimeStr.split(" ")[1];
      const [hours, minutes] = timePart.split(":");
      const h = parseInt(hours, 10);
      const ampm = h >= 12 ? "PM" : "AM";
      const displayHours = h % 12 || 12;
      return `${displayHours}:${minutes} ${ampm}`;
    } catch {
      return dateTimeStr;
    }
  };

  const money = (val) => `$${Number(val || 0).toLocaleString()}`;

  const bestReturnFlight = flights?.return?.reduce(
    (best, current) =>
      !best || current.value_score > best.value_score ? current : best,
    null,
  );

  const sortedOutbound = React.useMemo(() => {
    const list = [...(flights.outbound || [])];

    const bestIndex = list.findIndex((f) => f.isBest);

    if (bestIndex === -1) return list;

    const best = list[bestIndex];
    const rest = list.filter((_, i) => i !== bestIndex);

    return [best, ...rest];
  }, [flights.outbound]);

  const sortedReturn = React.useMemo(() => {
    const list = [...(flights.return || [])];

    const bestIndex = list.findIndex(
      (f) => f.flight_id === bestReturnFlight?.flight_id,
    );

    if (bestIndex === -1) return list;

    const best = list[bestIndex];
    const rest = list.filter((_, i) => i !== bestIndex);

    return [best, ...rest];
  }, [flights.return, bestReturnFlight]);

  if (loading) {
    return (
      <div className="w-full max-w-[1400px] mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* LEFT SKELETON */}
          <div className="lg:col-span-8 space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-40 bg-slate-200 dark:bg-slate-800 rounded-2xl animate-pulse"
              />
            ))}
          </div>

          {/* RIGHT SKELETON */}
          <div className="lg:col-span-4 space-y-4">
            <div className="h-40 bg-slate-200 dark:bg-slate-800 rounded-2xl animate-pulse" />
            <div className="h-56 bg-slate-200 dark:bg-slate-800 rounded-2xl animate-pulse" />
            <div className="h-32 bg-slate-200 dark:bg-slate-800 rounded-2xl animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen w-full bg-[#FAFAFC] dark:bg-[#080C14] antialiased font-sans text-slate-800 dark:text-slate-200 transition-colors duration-300 pb-24 lg:pb-12">
      <div className="w-full max-w-[1400px] mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-start gap-4">
            <button
              onClick={() => navigate(-1)}
              className="hidden md:flex p-2.5 bg-white dark:bg-[#151F32] rounded-xl border border-slate-200/60 dark:border-slate-800/80 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white shadow-2xs hover:shadow-xs transition-all cursor-pointer shrink-0 mt-1"
            >
              <ChevronLeft size={20} />
            </button>

            <div className="flex-1">
              <div className="flex items-center gap-2 text-xs font-bold text-[#D4AF37] tracking-widest uppercase mb-1">
                <Sparkles size={14} className="animate-pulse" /> Ranked by
                value, your points & experience
              </div>
              <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                Top Flight Results
              </h1>
            </div>
          </div>

          <div className="bg-white dark:bg-[#151F32] rounded-2xl p-4 sm:p-5 border border-slate-200/60 dark:border-slate-800/50 flex items-center justify-between shadow-xs backdrop-blur-md transition-all">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                <Info size={18} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400 dark:text-slate-500">
                  Active Route Parameters
                </p>
                <p className="text-sm font-bold text-slate-700 dark:text-slate-200 mt-0.5">
                  {formData
                    ? `${formData.origin_code} → ${formData.destination_code} • ${
                        formData?.departure_date
                          ? moment(formData.departure_date).format("DD MMM")
                          : "Not set"
                      } • ${
                        formData.trip_type === "roundtrip"
                          ? "Round Trip"
                          : "One Way"
                      } • ${formData.passengers} Pax • ${formData.cabin_class?.charAt(0).toUpperCase() + formData.cabin_class?.slice(1)}`
                    : "ORD → DEL • 21 Jun • One Way • 1 Passenger • Economy Class"}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/search")}
              className="p-2.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10 rounded-xl transition-all ml-2 shrink-0 cursor-pointer border border-transparent hover:border-amber-200 dark:hover:border-amber-500/20 shadow-2xs"
            >
              <Edit2 size={16} />
            </button>
          </div>

          <div className="flex gap-3 w-full md:w-auto">
            <button className="hidden flex items-center justify-center gap-2 px-4 h-11 bg-white dark:bg-[#111823] border border-slate-200 dark:border-slate-800/80 rounded-xl text-xs font-bold shadow-xs hover:bg-slate-50 dark:hover:bg-slate-800 transition-all cursor-pointer">
              <Filter size={14} className="text-slate-400" />
              <span>Filters</span>
            </button>
            <button
              onClick={handleSort}
              className="flex items-center justify-center gap-2 px-4 h-11 bg-white dark:bg-[#111823] border border-slate-200 dark:border-slate-800/80 rounded-xl text-xs font-bold shadow-xs hover:bg-slate-50 dark:hover:bg-slate-800 transition-all cursor-pointer"
            >
              <ArrowUpDown size={14} className="text-[#D4AF37]" />
              <span>
                Sort by:{" "}
                <span className="text-[#D4AF37]">
                  {sortBy === "cpp" ? "Best Value" : sortBy.toUpperCase()}
                </span>
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8 space-y-6">
            {flights?.outbound?.length > 0 ? (
              sortedOutbound?.map((flight, index) => {
                const isBest = flight.isBest;
                const hasEnoughPoints = flight.can_book_now;

                const badgeColor = flight.badge?.includes("BEST")
                  ? "emerald"
                  : flight.badge?.includes("GOOD")
                    ? "blue"
                    : "amber";

                return (
                  <div
                    key={index}
                    className={`relative bg-white dark:bg-[#111823] rounded-2xl border transition-all duration-300 ${
                      isBest
                        ? "border-voyage-cyan shadow-[0_0_0_2px_rgba(16,185,129,0.15)]"
                        : "border-slate-200/80 dark:border-slate-800/80"
                    }`}
                  >
                    {/* TOP BAR - Moved OUTBOUND inside here for better layout */}
                    <div className="bg-slate-50 dark:bg-[#161F2E] rounded-t-2xl border-b border-slate-100 dark:border-slate-800/60 p-4">
                      {/* Outbound Label */}
                      <div className="text-[11px] font-black text-blue-500 uppercase mb-1">
                        OUTBOUND
                      </div>

                      {/* Header Info */}
                      <div className="flex justify-between items-center">
                        <span className="text-[11px] font-black tracking-wider uppercase text-slate-400 dark:text-slate-500">
                          #{index + 1} • {flight.segment_type}
                          {isBest && (
                            <span className="ml-2 px-2 py-0.5 text-[9px] font-black uppercase rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
                              ⭐ Best Deal
                            </span>
                          )}
                        </span>

                        <span className="px-2 py-0.5 bg-[#8E44AD]/10 dark:bg-[#8E44AD]/20 rounded text-[9px] font-black text-[#8E44AD] uppercase tracking-wider">
                          {flight.cabin}
                        </span>
                      </div>
                    </div>

                    <div className="p-4 sm:p-6 flex flex-col lg:flex-row gap-6 justify-between items-stretch">
                      {/* LEFT: ROUTE */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-1">
                        <div className="w-14 h-14 rounded-xl bg-slate-50 dark:bg-[#161F2E] p-2 border flex items-center justify-center">
                          <img
                            src={flight.airline_logo}
                            className="w-full h-full object-contain"
                            alt="airline"
                          />
                        </div>

                        <div className="space-y-1.5 w-full">
                          {/* ROUTE + BADGE */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xl font-black">
                              {flight.departure_airport}
                            </span>

                            <span className="text-slate-400">→</span>

                            <span className="text-xl font-black">
                              {flight.arrival_airport}
                            </span>

                            {/* <span
                              className={`ml-2 px-2 py-0.5 rounded text-[10px] font-black uppercase
                      ${
                        badgeColor === "emerald"
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
                          : badgeColor === "blue"
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400"
                            : "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400"
                      }`}
                            >
                              {flight.badge}
                            </span> */}
                          </div>

                          {/* TIME + DETAILS */}
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 font-semibold">
                            <span>{formatTime(flight.departure_time)}</span>
                            <span>•</span>
                            <span>{formatTime(flight.arrival_time)}</span>
                            <span>•</span>
                            <span>{formatDuration(flight.total_duration)}</span>
                            <span>•</span>
                            <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[10px]">
                              {flight.stops === 0
                                ? "Nonstop"
                                : `${flight.stops} Stop`}
                            </span>
                          </div>

                          {/* WHY THIS FLIGHT */}
                          <p className="text-[11px] text-slate-400">
                            {flight.value_score >= 80
                              ? "🔥 High-value estimated redemption vs market average"
                              : flight.stops === 0
                                ? "✈️ Direct routing with stable award pricing"
                                : "💡 Balanced estimated value for this route"}
                          </p>
                        </div>
                      </div>

                      {/* MIDDLE: POINTS */}
                      <div className="flex sm:items-center justify-between lg:justify-end gap-6 border-t lg:border-l border-slate-100 dark:border-slate-800/80 pt-4 lg:pt-0 lg:pl-6 min-w-[180px]">
                        <div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-2xl font-black">
                              {Number(flight.required_points).toLocaleString()}
                            </span>
                            <span className="text-xs text-slate-400">pts</span>
                          </div>

                          <p className="text-[11px] text-slate-400">
                            Est. cash value: {money(flight.price)}
                          </p>

                          {/* NEW: TRUST LINE */}
                          <p className="text-[10px] text-slate-500 mt-1">
                            ✨ Estimated redemption value based on live award
                            pricing
                          </p>
                        </div>

                        <div className="text-right">
                          {hasEnoughPoints ? (
                            <div className="px-2.5 py-1 bg-emerald-50 text-emerald-600 rounded-lg text-[11px] font-black uppercase">
                              🔥 Book Now
                            </div>
                          ) : (
                            <div className="px-2.5 py-1 bg-amber-50 text-amber-600 rounded-lg text-[11px] font-black uppercase">
                              ⚡{" "}
                              {Number(
                                flight.extra_points_needed,
                              ).toLocaleString()}{" "}
                              pts left
                            </div>
                          )}

                          <p className="text-[10px] text-slate-400 mt-1">
                            {hasEnoughPoints
                              ? "Ready to redeem"
                              : "Keep earning points"}
                          </p>
                        </div>
                      </div>

                      {/* RIGHT: SCORE */}
                      <div className="flex flex-row lg:flex-col items-center justify-between lg:justify-center gap-3 border-t lg:border-l border-slate-100 dark:border-slate-800/80 pt-4 lg:pt-0 lg:pl-6 min-w-[100px]">
                        {/* SCORE */}
                        <div className="relative w-14 h-14 flex items-center justify-center">
                          <svg
                            className="w-full h-full transform -rotate-90"
                            viewBox="0 0 36 36"
                          >
                            <path
                              className="text-slate-200 dark:text-slate-800"
                              strokeWidth="2.5"
                              stroke="currentColor"
                              fill="none"
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831"
                            />
                            <path
                              className="text-emerald-500"
                              strokeWidth="2.5"
                              strokeDasharray={`${flight.value_score}, 100`}
                              strokeLinecap="round"
                              stroke="currentColor"
                              fill="none"
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831"
                            />
                          </svg>

                          <div className="absolute text-center">
                            <div className="text-sm font-black">
                              {flight.value_score}
                            </div>
                            <div className="text-[8px] text-slate-400">
                              Score
                            </div>
                          </div>
                        </div>

                        <div className="text-center">
                          <span className="text-[9px] uppercase text-slate-400 font-bold">
                            CPP
                          </span>
                          <div className="text-xs font-bold text-emerald-600">
                            {flight.cpp_value}¢ CPP
                          </div>

                          <p className="text-[9px] text-slate-400">
                            Est. value per point
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* ACTIONS */}
                    <div className="px-4 py-4 sm:px-6 bg-slate-50/60 dark:bg-[#141C29]/50 border-t flex flex-col gap-4">
                      <div className="grid grid-cols-3 sm:flex sm:justify-end gap-2">
                        <button
                          onClick={() =>
                            navigate("/deals-details", { state: flight })
                          }
                          className={`h-10 px-4 rounded-lg text-xs font-semibold transition active:scale-[0.98]
                               ${
                                 isBest
                                   ? "bg-voyage-cyan text-white shadow-md"
                                   : "bg-slate-900 text-white hover:bg-slate-800"
                               }`}
                        >
                          View Deal
                        </button>

                        {/* SHARE BUTTON (unchanged behavior, better UX) */}
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/deals-details?search_id=${flight.search_id}`;

                            window.open(
                              `https://wa.me/?text=${encodeURIComponent(
                                `✈️ Flight Deal\n${url}`,
                              )}`,
                              "_blank",
                            );
                          }}
                          className="h-10 px-4 rounded-lg border text-xs font-medium
                 text-emerald-600 border-emerald-500
                 hover:bg-emerald-50 dark:hover:bg-emerald-500/10
                 transition active:scale-[0.98]"
                        >
                          Share
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="w-full bg-white dark:bg-[#111823] border border-slate-200 dark:border-slate-800 rounded-2xl p-10 flex flex-col items-center justify-center text-center shadow-sm">
                <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-[#1A2332] flex items-center justify-center mb-4">
                  <Plane size={30} className="text-slate-400" />
                </div>

                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  No Flights Available
                </h3>

                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-md">
                  We couldn’t find any reward flight deals for your selected
                  route and dates. Try changing your destination, travel dates,
                  or cabin class.
                </p>

                <button
                  onClick={() => navigate(-1)}
                  className="mt-5 px-5 h-10 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-semibold hover:opacity-90 transition-all"
                >
                  Modify Search
                </button>
              </div>
            )}

            {flights?.return?.length > 0 ? (
              sortedReturn?.map((flight, index) => {
                const isBests =
                  flight.flight_id === bestReturnFlight?.flight_id;
                const hasEnoughPoints = flight.can_book_now;

                const badgeColor = flight.badge?.includes("BEST")
                  ? "emerald"
                  : flight.badge?.includes("GOOD")
                    ? "blue"
                    : "amber";

                return (
                  <div
                    key={index}
                    className={`relative bg-white dark:bg-[#111823] rounded-2xl border transition-all duration-300 ${
                      isBests
                        ? "border-voyage-cyan shadow-[0_0_0_2px_rgba(16,185,129,0.15)]"
                        : "border-slate-200/80 dark:border-slate-800/80"
                    }`}
                  >
                    <div className="bg-slate-50 dark:bg-[#161F2E] rounded-t-2xl border-b border-slate-100 dark:border-slate-800/60 p-4">
                      {/* Outbound Label */}
                      <div className="text-[11px] font-black text-purple-500 uppercase mb-1">
                        RETURN
                      </div>

                      {/* Header Info */}
                      <div className="flex justify-between items-center">
                        <span className="text-[11px] font-black tracking-wider uppercase text-slate-400 dark:text-slate-500">
                          #{index + 1} • {flight.segment_type}
                          {isBests && (
                            <span className="ml-2 px-2 py-0.5 text-[9px] font-black uppercase rounded bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-400">
                              ⭐ Best Return Deal
                            </span>
                          )}
                        </span>

                        <span className="px-2 py-0.5 bg-[#8E44AD]/10 dark:bg-[#8E44AD]/20 rounded text-[9px] font-black text-[#8E44AD] uppercase tracking-wider">
                          {flight.cabin}
                        </span>
                      </div>
                    </div>

                    <div className="p-4 sm:p-6 flex flex-col lg:flex-row gap-6 justify-between items-stretch">
                      {/* LEFT: ROUTE */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-1">
                        <div className="w-14 h-14 rounded-xl bg-slate-50 dark:bg-[#161F2E] p-2 border flex items-center justify-center">
                          <img
                            src={flight.airline_logo}
                            className="w-full h-full object-contain"
                            alt="airline"
                          />
                        </div>

                        <div className="space-y-1.5 w-full">
                          {/* ROUTE + BADGE */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xl font-black">
                              {flight.departure_airport}
                            </span>

                            <span className="text-slate-400">→</span>

                            <span className="text-xl font-black">
                              {flight.arrival_airport}
                            </span>

                            {/* <span
                              className={`ml-2 px-2 py-0.5 rounded text-[10px] font-black uppercase
                      ${
                        badgeColor === "emerald"
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
                          : badgeColor === "blue"
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400"
                            : "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400"
                      }`}
                            >
                              {flight.badge}
                            </span> */}
                          </div>

                          {/* TIME + DETAILS */}
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 font-semibold">
                            <span>{formatTime(flight.departure_time)}</span>
                            <span>•</span>
                            <span>{formatTime(flight.arrival_time)}</span>
                            <span>•</span>
                            <span>{formatDuration(flight.total_duration)}</span>
                            <span>•</span>
                            <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[10px]">
                              {flight.stops === 0
                                ? "Nonstop"
                                : `${flight.stops} Stop`}
                            </span>
                          </div>

                          {/* WHY THIS FLIGHT */}
                          <p className="text-[11px] text-slate-400">
                            {flight.value_score >= 80
                              ? "🔥 High-value estimated redemption vs market average"
                              : flight.stops === 0
                                ? "✈️ Direct routing with stable award pricing"
                                : "💡 Balanced estimated value for this route"}
                          </p>
                        </div>
                      </div>

                      {/* MIDDLE: POINTS */}
                      <div className="flex sm:items-center justify-between lg:justify-end gap-6 border-t lg:border-l border-slate-100 dark:border-slate-800/80 pt-4 lg:pt-0 lg:pl-6 min-w-[180px]">
                        <div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-2xl font-black">
                              {Number(flight.required_points).toLocaleString()}
                            </span>
                            <span className="text-xs text-slate-400">pts</span>
                          </div>

                          <p className="text-[11px] text-slate-400">
                            Est. cash value: {money(flight.price)}
                          </p>

                          {/* NEW: TRUST LINE */}
                          <p className="text-[10px] text-slate-500 mt-1">
                            ✨ Estimated redemption value based on live award
                            pricing
                          </p>
                        </div>

                        <div className="text-right">
                          {hasEnoughPoints ? (
                            <div className="px-2.5 py-1 bg-emerald-50 text-emerald-600 rounded-lg text-[11px] font-black uppercase">
                              🔥 Book Now
                            </div>
                          ) : (
                            <div className="px-2.5 py-1 bg-amber-50 text-amber-600 rounded-lg text-[11px] font-black uppercase">
                              ⚡{" "}
                              {Number(
                                flight.extra_points_needed,
                              ).toLocaleString()}{" "}
                              pts left
                            </div>
                          )}

                          <p className="text-[10px] text-slate-400 mt-1">
                            {hasEnoughPoints
                              ? "Ready to redeem"
                              : "Keep earning points"}
                          </p>
                        </div>
                      </div>

                      {/* RIGHT: SCORE */}
                      <div className="flex flex-row lg:flex-col items-center justify-between lg:justify-center gap-3 border-t lg:border-l border-slate-100 dark:border-slate-800/80 pt-4 lg:pt-0 lg:pl-6 min-w-[100px]">
                        {/* SCORE */}
                        <div className="relative w-14 h-14 flex items-center justify-center">
                          <svg
                            className="w-full h-full transform -rotate-90"
                            viewBox="0 0 36 36"
                          >
                            <path
                              className="text-slate-200 dark:text-slate-800"
                              strokeWidth="2.5"
                              stroke="currentColor"
                              fill="none"
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831"
                            />
                            <path
                              className="text-emerald-500"
                              strokeWidth="2.5"
                              strokeDasharray={`${flight.value_score}, 100`}
                              strokeLinecap="round"
                              stroke="currentColor"
                              fill="none"
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831"
                            />
                          </svg>

                          <div className="absolute text-center">
                            <div className="text-sm font-black">
                              {flight.value_score}
                            </div>
                            <div className="text-[8px] text-slate-400">
                              Score
                            </div>
                          </div>
                        </div>

                        <div className="text-center">
                          <span className="text-[9px] uppercase text-slate-400 font-bold">
                            CPP
                          </span>
                          <div className="text-xs font-bold text-emerald-600">
                            {flight.cpp_value}¢ CPP
                          </div>

                          <p className="text-[9px] text-slate-400">
                            Est. value per point
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* ACTIONS */}
                    <div className="px-4 py-4 sm:px-6 bg-slate-50/60 dark:bg-[#141C29]/50 border-t flex flex-col gap-4">
                      <div className="grid grid-cols-3 sm:flex sm:justify-end gap-2">
                        {/* VIEW DEAL (keeps isBest logic) */}
                        <button
                          onClick={() =>
                            navigate("/deals-details", { state: flight })
                          }
                          className={`h-10 px-4 rounded-lg text-xs font-semibold transition active:scale-[0.98]
                               ${
                                 isBests
                                   ? "bg-voyage-cyan text-white shadow-md"
                                   : "bg-slate-900 text-white hover:bg-slate-800"
                               }`}
                        >
                          View Deal
                        </button>

                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/deals-details?search_id=${flight.search_id}`;

                            window.open(
                              `https://wa.me/?text=${encodeURIComponent(
                                `✈️ Flight Deal\n${url}`,
                              )}`,
                              "_blank",
                            );
                          }}
                          className="h-10 px-4 rounded-lg border text-xs font-medium
                          text-emerald-600 border-emerald-500
                           hover:bg-emerald-50 dark:hover:bg-emerald-500/10
                           transition active:scale-[0.98]"
                        >
                          Share
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <></>
            )}
          </div>

          <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-6">
            {summary && (
              <div className="p-6 bg-white dark:bg-[#111823] rounded-2xl shadow-xs border border-slate-200/80 dark:border-slate-800/80 relative overflow-hidden">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <span className="text-[10px] font-black tracking-widest uppercase text-slate-400 dark:text-slate-500">
                      Your Travel Power
                    </span>
                    <div className="text-2xl font-black text-slate-900 dark:text-white tracking-tight mt-0.5">
                      {Number(summary.available_points).toLocaleString()}
                    </div>
                    <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500">
                      Available Points
                    </p>
                  </div>

                  <div className="text-right">
                    <span className="inline-block text-[#D4AF37] p-2 bg-[rgba(212,175,55,0.08)] rounded-xl">
                      <Award size={20} />
                    </span>
                    <div className="text-sm font-black text-slate-900 dark:text-white mt-1">
                      {money(summary.redeemable_cash_value)}
                    </div>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">
                      Est. Travel Value
                    </p>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800/60">
                  <span className="text-xs font-black tracking-wider uppercase text-[#D4AF37]">
                    {summary.travel_power_level || "PREMIUM TRAVELER"}
                  </span>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 leading-relaxed">
                    {summary.estimated_message ||
                      "You're unlocking amazing premium travel paths."}
                  </p>
                </div>

                <div className="mt-4 w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#D4AF37] to-[#bfa032] rounded-full"
                    style={{ width: "76%" }}
                  />
                </div>
                <div className="flex justify-between items-center mt-1.5 text-[10px] font-bold text-slate-400">
                  <span>76% to Elite Status</span>
                </div>
              </div>
            )}

            {bestDeal && (
              <div className="p-6 bg-white dark:bg-[#111823] rounded-2xl shadow-xs border border-slate-200/80 dark:border-slate-800/80 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-[rgba(142,68,173,0.04)] rounded-bl-full pointer-events-none" />
                <h3 className="text-xs font-black tracking-widest uppercase text-slate-400 dark:text-slate-500 mb-3 flex items-center gap-1.5">
                  <Milestone size={14} className="text-[#8E44AD]" /> Business
                  Class Goal
                </h3>

                <div className="space-y-4">
                  <div>
                    <div className="text-xl font-black text-[#8E44AD] tracking-tight">
                      {Number(
                        bestDeal.estimated_business_class_points || 106560,
                      ).toLocaleString()}{" "}
                      <span className="text-xs font-bold text-slate-400">
                        pts
                      </span>
                    </div>
                    <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 leading-tight mt-0.5">
                      Your target path for luxury long-haul lie-flat seating
                      configurations.
                    </p>
                  </div>

                  <div className="space-y-1">
                    <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#8E44AD] rounded-full"
                        style={{ width: "62%" }}
                      />
                    </div>
                    <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 dark:text-slate-500 pt-0.5">
                      <span>Earn remaining points</span>
                      <span className="text-[#8E44AD]">36,560 pts to go</span>
                    </div>
                  </div>

                  <div className="p-3 bg-[#8E44AD]/5 rounded-xl border border-[#8E44AD]/10 text-[11px] font-semibold text-slate-600 dark:text-slate-400 leading-relaxed">
                    💡{" "}
                    <span className="text-slate-900 dark:text-white font-bold">
                      JustForPoints Insight:
                    </span>{" "}
                    {bestDeal.business_class_insight ||
                      "Earn points to target Business Class experiences."}
                  </div>
                </div>
              </div>
            )}

            <div className="p-5 bg-gradient-to-br from-slate-900 to-[#111622] text-white rounded-2xl shadow-md border border-slate-800 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/[0.02] rounded-bl-full pointer-events-none" />
              <h3 className="text-[10px] font-black tracking-widest uppercase text-slate-400 mb-3 flex items-center gap-1">
                <TrendingUp size={12} className="text-[#D4AF37]" />{" "}
                JustForPoints Insight
              </h3>
              <p className="text-xs font-medium text-slate-300 leading-relaxed">
                Your{" "}
                <span className="text-[#D4AF37] font-bold">
                  Amex Membership Rewards
                </span>{" "}
                ecosystem shows exceptionally flat value curves on transatlantic
                legs today. Booking route option #1 secures premium routing
                index metrics.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedemptionResults;
